import type { TimeRange } from "./types/api";

/**
 * Bucket width requested from the API per range, in seconds. 150-odd buckets keep every
 * chart calm and leave each one a few pixels wide.
 */
export const BUCKET_SECONDS: Record<TimeRange, number> = {
  "24h": 600,
  "7d": 3600,
  "30d": 4 * 3600,
  "1y": 86400
};

/** 24 hours in 15-minute steps: enough shape for a stat tile without the minute-to-minute noise. */
export const SPARK_SECONDS = 900;

export interface BucketAxis {
  /** Bucket start times in milliseconds, evenly spaced and without holes. */
  times: number[];
  stepMs: number;
  /** Position on the axis of the bucket that starts at `timestamp` (seconds). */
  slot: (timestamp: number) => number;
}

/**
 * A contiguous time axis covering every timestamp given. The API leaves out buckets that
 * have no samples, and requests for different metrics can start or end a bucket apart;
 * laying the points out on one axis turns those holes into gaps in the line.
 */
export function bucketAxis(timestamps: Iterable<number>, stepSeconds: number): BucketAxis {
  let start = Infinity;
  let end = -Infinity;
  for (const timestamp of timestamps) {
    if (timestamp < start) start = timestamp;
    if (timestamp > end) end = timestamp;
  }
  const stepMs = stepSeconds * 1000;
  if (!Number.isFinite(start) || !(stepSeconds > 0)) return { times: [], stepMs, slot: () => -1 };
  const length = Math.round((end - start) / stepSeconds) + 1;
  return {
    times: Array.from({ length }, (_, index) => (start + index * stepSeconds) * 1000),
    stepMs,
    slot: (timestamp) => Math.round((timestamp - start) / stepSeconds)
  };
}

/** One value per axis bucket, null where the series has no point. */
export function fillAxis<T extends { timestamp: number }>(
  axis: BucketAxis,
  points: ReadonlyArray<T>,
  value: (point: T) => number | null | undefined
): Array<number | null> {
  const row: Array<number | null> = new Array(axis.times.length).fill(null);
  for (const point of points) {
    const slot = axis.slot(point.timestamp);
    if (slot < 0 || slot >= row.length) continue;
    const next = value(point);
    row[slot] = next != null && Number.isFinite(next) ? next : null;
  }
  return row;
}

/** Linear-interpolated quantile of an ascending array. */
export function quantile(sorted: ReadonlyArray<number>, q: number): number | null {
  if (!sorted.length) return null;
  const position = (sorted.length - 1) * Math.min(1, Math.max(0, q));
  const low = Math.floor(position);
  const high = Math.min(low + 1, sorted.length - 1);
  return sorted[low] + (sorted[high] - sorted[low]) * (position - low);
}

/**
 * Smallest "nice" step (1, 2, 2.5, 5 × 10ⁿ) that is at least `raw`. With `whole`, steps are
 * whole numbers from 1, 2, 5 × 10ⁿ only.
 */
function niceStep(raw: number, whole: boolean): number {
  if (!(raw > 0)) return 1;
  const floor = whole ? Math.max(raw, 1) : raw;
  const power = 10 ** Math.floor(Math.log10(floor));
  const fraction = floor / power;
  const nice =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 && !whole ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * power;
}

/**
 * Zero-based axis ticks with a rounded top that clears `max`. Pass `whole` for things that
 * are counted rather than measured, so the axis never shows half an event.
 */
export function linearTicks(max: number, count = 5, whole = false): number[] {
  const safeMax = Number.isFinite(max) && max > 0 ? max : 1;
  const step = niceStep(safeMax / count, whole);
  const top = Math.ceil(safeMax / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= top + step / 2; value += step) {
    ticks.push(Number(value.toPrecision(12)));
  }
  return ticks;
}

export interface TimeTick {
  time: number;
  label: string;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Tick marks on round local-time boundaries (hours, midnights, firsts of the month),
 * using the finest unit that still fits `maxTicks` labels.
 */
export function timeTicks(start: number, end: number, maxTicks: number): TimeTick[] {
  const span = end - start;
  if (!(span > 0)) return [];
  const limit = Math.max(2, maxTicks);

  for (const hours of [1, 2, 3, 6, 12]) {
    if (span / (hours * HOUR) > limit) continue;
    const ticks: TimeTick[] = [];
    const cursor = new Date(start);
    cursor.setMinutes(0, 0, 0);
    cursor.setHours(Math.floor(cursor.getHours() / hours) * hours);
    while (cursor.getTime() <= end) {
      if (cursor.getTime() >= start) {
        const midnight = cursor.getHours() === 0;
        ticks.push({
          time: cursor.getTime(),
          label: midnight
            ? `${cursor.getMonth() + 1}/${cursor.getDate()}`
            : `${pad(cursor.getHours())}:00`
        });
      }
      cursor.setHours(cursor.getHours() + hours);
    }
    return ticks;
  }

  for (const days of [1, 2, 7, 14]) {
    if (span / (days * DAY) > limit) continue;
    const ticks: TimeTick[] = [];
    // Count back from the newest midnight so the most recent days keep their labels.
    const cursor = new Date(end);
    cursor.setHours(0, 0, 0, 0);
    while (cursor.getTime() >= start) {
      ticks.unshift({ time: cursor.getTime(), label: `${cursor.getMonth() + 1}/${cursor.getDate()}` });
      cursor.setDate(cursor.getDate() - days);
    }
    return ticks;
  }

  for (const months of [1, 2, 3, 6, 12]) {
    if (span / (months * 30.44 * DAY) > limit && months !== 12) continue;
    const ticks: TimeTick[] = [];
    const cursor = new Date(end);
    cursor.setHours(0, 0, 0, 0);
    cursor.setDate(1);
    while (cursor.getTime() >= start) {
      ticks.unshift({ time: cursor.getTime(), label: `${cursor.getMonth() + 1}月` });
      cursor.setMonth(cursor.getMonth() - months);
    }
    return ticks;
  }
  return [];
}

const LOG_STEPS = [1, 2, 5];

/** Widen a positive range to the nearest 1-2-5 boundaries for a logarithmic axis. */
export function logDomain(low: number, high: number): [number, number] {
  const safeLow = low > 0 && Number.isFinite(low) ? low : 1;
  const safeHigh = high > safeLow && Number.isFinite(high) ? high : safeLow * 10;
  const lowPower = 10 ** Math.floor(Math.log10(safeLow));
  let floor = lowPower;
  for (const step of LOG_STEPS) if (step * lowPower <= safeLow * (1 + 1e-9)) floor = step * lowPower;
  const highPower = 10 ** Math.floor(Math.log10(safeHigh));
  let ceiling = highPower * 10;
  for (const step of [...LOG_STEPS].reverse()) {
    if (step * highPower >= safeHigh * (1 - 1e-9)) ceiling = step * highPower;
  }
  return [floor, ceiling > floor ? ceiling : floor * 10];
}

export interface LogTick {
  value: number;
  /** Powers of ten; the 2 and 5 steps in between are minor. */
  major: boolean;
}

export function logTicks(domain: readonly [number, number]): LogTick[] {
  const [low, high] = domain;
  const ticks: LogTick[] = [];
  for (let power = Math.floor(Math.log10(low)); power <= Math.ceil(Math.log10(high)); power += 1) {
    for (const step of LOG_STEPS) {
      const value = Number((step * 10 ** power).toPrecision(12));
      if (value < low * (1 - 1e-9) || value > high * (1 + 1e-9)) continue;
      ticks.push({ value, major: step === 1 });
    }
  }
  return ticks;
}

/** Position of `value` along a logarithmic axis, clamped to 0-1. */
export function logPosition(value: number, domain: readonly [number, number]): number {
  const [low, high] = domain;
  if (!(value > 0) || !(high > low)) return 0;
  const position = (Math.log(value) - Math.log(low)) / (Math.log(high) - Math.log(low));
  return Math.min(1, Math.max(0, position));
}
